/**
 * Localized copy for the cross-school cart guard: shown when the customer
 * lands on ANOTHER school's shop while the cart still holds items from the
 * previous school. Keyed by API brand slug, English fallback.
 */
export const SCHOOL_SWITCH_COPY = {
  clsdk: {
    title: 'Skift skoleshop?',
    message: 'Du kan kun købe én type produkter ad gangen. Hvis du fortsætter, tømmes din indkøbskurv.',
    backLabel: 'Tilbage',
    confirmLabel: 'Tøm kurven & fortsæt',
  },
  clsse: {
    title: 'Byta skolshop?',
    message: 'Du kan bara köpa en typ av produkter åt gången. Om du fortsätter töms din varukorg.',
    backLabel: 'Tillbaka',
    confirmLabel: 'Töm & fortsätt',
  },
  abcse: {
    title: 'Byta skolshop?',
    message: 'Du kan bara köpa en typ av produkter åt gången. Om du fortsätter töms din varukorg.',
    backLabel: 'Tillbaka',
    confirmLabel: 'Töm & fortsätt',
  },
  clsfi: {
    title: 'Vaihdetaanko koulukauppaa?',
    message: 'Voit ostaa kerralla vain yhden tyypin tuotteita. Jos jatkat, ostoskorisi tyhjennetään.',
    backLabel: 'Takaisin',
    confirmLabel: 'Tyhjennä ja jatka',
  },
  en: {
    title: 'Switch school shop?',
    message: 'You can only buy one type of products at a time. To continue, your shopping cart will be erased.',
    backLabel: 'Back',
    confirmLabel: 'Erase & Continue',
  },
};

export const schoolSwitchCopyFor = (brandSlug) => SCHOOL_SWITCH_COPY[brandSlug] || SCHOOL_SWITCH_COPY.en;

/**
 * Same guard, class flavour: shown when a class-shop link (?class=) targets a
 * different class than the one the cart is bound to. The BE rotates the cart to
 * a fresh empty one on any class change, so a link shared between classes must
 * not silently wipe the customer's basket.
 */
export const CLASS_SWITCH_COPY = {
  clsdk: {
    title: 'Skift klasse?',
    message: 'Din kurv indeholder varer bestilt til en anden klasse. Hvis du fortsætter, tømmes din indkøbskurv.',
    backLabel: 'Behold klassen',
    confirmLabel: 'Tøm kurven & skift',
  },
  clsfi: {
    title: 'Vaihdetaanko luokkaa?',
    message: 'Ostoskorissasi on toiselle luokalle tilattuja tuotteita. Jos jatkat, ostoskorisi tyhjennetään.',   // FI-TODO review
    backLabel: 'Säilytä luokka',
    confirmLabel: 'Tyhjennä kori & vaihda',
  },
  en: {
    title: 'Switch class?',
    message: 'Your cart holds items ordered for another class. To continue, your shopping cart will be erased.',
    backLabel: 'Keep the class',
    confirmLabel: 'Erase & switch',
  },
};

export const classSwitchCopyFor = (brandSlug) => CLASS_SWITCH_COPY[brandSlug] || CLASS_SWITCH_COPY.en;

/**
 * Same guard, shop flavour (2026-09-14): the customer is identified in one of
 * the school's shops but adds a product that belongs to its OTHER shop
 * (Studieturstrøjer ↔ Vogntrøjer). An order carries exactly one shop, so the
 * cart must be emptied to switch. Names are interpolated when known.
 */
export const SHOP_SWITCH_COPY = {
  clsdk: {
    title: (to) => (to ? `Skift til ${to}?` : 'Skift shop?'),
    message: (from, to) => `Din kurv indeholder varer fra ${from || 'en anden shop'}. Du kan kun bestille fra én shop ad gangen – hvis du fortsætter, tømmes din kurv, og du kan bestille fra ${to || 'den nye shop'}.`,
    backLabel: 'Behold min kurv',
    confirmLabel: 'Tøm kurven & skift',
  },
  clsse: {
    title: (to) => (to ? `Byta till ${to}?` : 'Byta shop?'),
    message: (from, to) => `Din varukorg innehåller varor från ${from || 'en annan shop'}. Du kan bara beställa från en shop åt gången – om du fortsätter töms din varukorg och du kan beställa från ${to || 'den nya shopen'}.`,
    backLabel: 'Behåll min varukorg',
    confirmLabel: 'Töm & byt',
  },
  abcse: {
    title: (to) => (to ? `Byta till ${to}?` : 'Byta shop?'),
    message: (from, to) => `Din varukorg innehåller varor från ${from || 'en annan shop'}. Du kan bara beställa från en shop åt gången – om du fortsätter töms din varukorg och du kan beställa från ${to || 'den nya shopen'}.`,
    backLabel: 'Behåll min varukorg',
    confirmLabel: 'Töm & byt',
  },
  clsfi: {
    title: (to) => (to ? `Vaihdetaanko kauppaan ${to}?` : 'Vaihdetaanko kauppaa?'),
    message: (from, to) => `Ostoskorissasi on tuotteita kaupasta ${from || 'toinen kauppa'}. Voit tilata vain yhdestä kaupasta kerrallaan – jos jatkat, ostoskorisi tyhjennetään ja voit tilata kaupasta ${to || 'uusi kauppa'}.`,
    backLabel: 'Säilytä ostoskori',
    confirmLabel: 'Tyhjennä ja vaihda',
  },
  en: {
    title: (to) => (to ? `Switch to ${to}?` : 'Switch shop?'),
    message: (from, to) => `Your cart holds items from ${from || 'another shop'}. You can only order from one shop at a time – if you continue, your cart is emptied and you can order from ${to || 'the new shop'}.`,
    backLabel: 'Keep my cart',
    confirmLabel: 'Empty cart & switch',
  },
};

/** Resolved copy object for SchoolSwitchPopup's `copy` prop. */
export const shopSwitchCopyFor = (brandSlug, { from, to } = {}) => {
  const c = SHOP_SWITCH_COPY[brandSlug] || SHOP_SWITCH_COPY.en;
  return { title: c.title(to), message: c.message(from, to), backLabel: c.backLabel, confirmLabel: c.confirmLabel };
};
